import { Link } from "react-router-dom";

import { useLive } from "../lib/use-live";
import type { AdminUser, Draw, Paged, ReportSummary, SystemStats } from "../types/api";
import {money} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import { LiveRound } from "../components/live-round";
import { useT } from "../context/locale";
import {
  Badge,
  Button,
  DrawStatusBadge,
  EmptyState,
  ErrorNote,
  Panel,
  ManualBadge,
  StatTile,
  Table,
  Td,
  Tr,
  UserStatusBadge,
} from "../components/ui";

export function Dashboard() {
  const dateTime = useDateTime();
  const relative = useRelative();
  const t = useT();
  const report = useLive<ReportSummary>("report", { excludeTest: true });
  const pending = useLive<Paged<AdminUser>>("review-queue", {
    status: "pending_review",
    perPage: 5,
  });
  const recent = useLive<{ items: Draw[] }>("recent-draws", { limit: 8 });
  const system = useLive<SystemStats>("system", {});

  const summary = report.data;

  return (
    <div className="space-y-5">
      {report.error ? (
        <ErrorNote message={report.error} onRetry={report.reload} />
      ) : null}

      <LiveRound />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={t("dashboard.turnover")}
          value={summary ? money(summary.turnoverCents) : "—"}
          hint={
            summary
              ? t("dashboard.settledBets", {
                  count: summary.betCount.toLocaleString(),
                })
              : t("common.loading")
          }
          tone="gold"
          dot
        />
        <StatTile
          label={t("dashboard.paidOut")}
          value={summary ? money(summary.payoutCents) : "—"}
          hint={
            summary
              ? t("dashboard.acrossRounds", {
                  count: summary.drawCount.toLocaleString(),
                })
              : t("common.loading")
          }
          tone="info"
          dot
        />
        <StatTile
          label={t("dashboard.grossProfit")}
          value={summary ? money(summary.grossProfitCents) : "—"}
          hint={
            summary
              ? t("dashboard.margin", { value: summary.marginPercent })
              : t("common.loading")
          }
          tone={summary && summary.grossProfitCents < 0 ? "danger" : "success"}
          dot
        />
        <StatTile
          label={t("dashboard.awaitingReview")}
          value={pending.data ? pending.data.total.toLocaleString() : "—"}
          hint={
            pending.data?.total
              ? t("dashboard.needsOperator")
              : t("dashboard.queueClear")
          }
          tone={pending.data?.total ? "danger" : "plain"}
          dot
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Panel
          eyebrow={t("nav.group.people")}
          title={t("dashboard.waitingApproval")}
          bodyClassName=""
          actions={
            <Link to="/review">
              <Button size="sm" variant="primary">
                {t("dashboard.openQueue")}
              </Button>
            </Link>
          }
        >
          {pending.data && pending.data.items.length === 0 ? (
            <EmptyState
              title={t("dashboard.nothingWaiting")}
              detail={t("dashboard.nothingWaitingDetail")}
            />
          ) : (
            <Table
              head={[
                t("common.player"),
                t("common.status"),
                t("common.joined"),
              ]}
              minWidth="24rem"
            >
              {(pending.data?.items ?? []).map((user) => (
                <Tr key={user.id}>
                  <Td>
                    <Link
                      to={`/players/${user.id}`}
                      className="font-semibold hover:underline"
                    >
                      {user.username}
                    </Link>
                  </Td>
                  <Td>
                    <UserStatusBadge status={user.status} />
                  </Td>
                  <Td className="text-dim text-[12px]">
                    {relative(user.createdAt)}
                  </Td>
                </Tr>
              ))}
            </Table>
          )}
        </Panel>

        <Panel eyebrow={t("dashboard.system")} title={t("dashboard.system")}>
          <dl className="space-y-2.5 text-[13px]">
            <Row
              label={t("dashboard.liveConnections")}
              value={system.data?.realtime.connections ?? "—"}
            />
            <Row
              label={t("dashboard.playersOnline")}
              value={system.data?.realtime.players ?? "—"}
            />
            <Row
              label={t("dashboard.staffOnline")}
              value={system.data?.realtime.admins ?? "—"}
            />
            <Row
              label={t("dashboard.responseCache")}
              value={
                system.data ? (
                  <Badge
                    tone={system.data.cache.enabled ? "success" : "neutral"}
                  >
                    {system.data.cache.enabled
                      ? t("common.entries", {
                          count: system.data.cache.entries,
                        })
                      : t("games.paused")}
                  </Badge>
                ) : (
                  "—"
                )
              }
            />
          </dl>
        </Panel>
      </div>

      <OnlineNow online={system.data?.realtime.online} />

      <Panel
        eyebrow={t("nav.draws")}
        title={t("dashboard.recentActivity")}
        bodyClassName=""
      >
        {recent.data && recent.data.items.length > 0 ? (
          <Table
            head={[
              t("common.round"),
              t("common.status"),
              t("draws.opened"),
              t("draws.drawn"),
              t("draws.source"),
            ]}
          >
            {recent.data.items.slice(0, 6).map((draw) => (
              <Tr key={draw.drawNo}>
                <Td className="money text-[12px]">#{draw.drawNo}</Td>
                <Td>
                  <DrawStatusBadge status={draw.status} />
                </Td>
                <Td className="text-dim text-[12px]">
                  {dateTime(draw.opensAt)}
                </Td>
                <Td className="text-dim text-[12px]">
                  {dateTime(draw.drawnAt)}
                </Td>
                <Td>
                  {draw.isManualResult ? (
                    <ManualBadge />
                  ) : (
                    <Badge tone="neutral">{t("draws.auto")}</Badge>
                  )}
                </Td>
              </Tr>
            ))}
          </Table>
        ) : (
          <EmptyState title={t("dashboard.noActivity")} />
        )}
      </Panel>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="money">{value}</dd>
    </div>
  );
}


function OnlineNow({ online }: { online?: SystemStats["realtime"]["online"] }) {
  const t = useT();

  const rows = [
    ...(online?.admins ?? []).map((account) => ({ ...account, staff: true })),
    ...(online?.players ?? []).map((account) => ({ ...account, staff: false })),
  ];

  return (
    <Panel
      eyebrow={t("dashboard.system")}
      title={t("dashboard.onlineNow")}
      bodyClassName=""
    >
      {rows.length === 0 ? (
        <EmptyState title={t("dashboard.nobodyOnline")} />
      ) : (
        <Table
          head={[
            t("dashboard.account"),
            t("common.role"),
            t("dashboard.connections"),
          ]}
          minWidth="24rem"
        >
          {rows.map((row) => (
            <Tr key={`${row.staff ? "a" : "p"}:${row.id}`}>
              <Td>
                {row.staff ? (
                  <span className="font-semibold">{row.username}</span>
                ) : (
                  <Link
                    to={`/players/${row.id}`}
                    className="font-semibold hover:underline"
                  >
                    {row.username}
                  </Link>
                )}
              </Td>
              <Td>
                <Badge tone={row.staff ? "info" : "success"}>
                  {row.staff ? t("dashboard.staff") : t("common.player")}
                </Badge>
              </Td>
              <Td className="money text-[12px]">{row.connections}</Td>
            </Tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}
